import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import { clsx } from 'clsx';
import React from 'react';

import {
  ILocationSearchAutocompleteListGroup,
  ILocationSearchAutocompleteListOption,
} from '@/hooks/useLocationSearchAutocompleteOptions';

import { LocationSearchAutocompleteListItem } from './LocationSearchAutocompleteListItem';

const DEFAULT_VERTICAL_OPTION_MAX = 3;
const DEFAULT_HORIZONTAL_OPTION_MAX = 6;
const DEFAULT_HORIZONTAL_OPTION_PER_COLUMN_MAX = 3;
const MAX_GROUPS = 2;

export enum ELocationSearchAutocompleteListVariant {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

interface LocationSearchAutocompleteListProps {
  groupedOptions: ILocationSearchAutocompleteListGroup[];
  getOptionProps?: (params: {
    option: ILocationSearchAutocompleteListOption;
    index: number;
  }) => React.HTMLAttributes<React.ElementType | HTMLDivElement>;
  onSelectOption?: (option: ILocationSearchAutocompleteListOption) => void;
  variant?: ELocationSearchAutocompleteListVariant;
  maxOptionsPerColumn?: number;
  maxGroups?: number;
  className?: string;
}

export const LocationSearchAutocompleteList: React.FC<LocationSearchAutocompleteListProps> = ({
  groupedOptions,
  getOptionProps,
  onSelectOption,
  variant = ELocationSearchAutocompleteListVariant.Horizontal,
  maxOptionsPerColumn,
  maxGroups = MAX_GROUPS,
  className = '',
}) => {
  const renderOption = (
    option: ILocationSearchAutocompleteListOption,
    index: number,
    totalOptions: number,
    isInLastGroup: boolean,
  ) => {
    const optionProps = getOptionProps
      ? getOptionProps({ option, index })
      : {
          onClick: () => onSelectOption?.(option),
          role: 'option',
        };

    return (
      <LocationSearchAutocompleteListItem
        key={option.value?.mapbox_id || `${option.label}-${index}`}
        option={option}
        optionIndex={index}
        optionProps={optionProps}
        totalOptions={totalOptions}
        variant={variant}
        defaultOptionPerColumnMax={
          variant === ELocationSearchAutocompleteListVariant.Horizontal
            ? DEFAULT_HORIZONTAL_OPTION_PER_COLUMN_MAX
            : undefined
        }
        isInLastGroup={isInLastGroup}
      />
    );
  };

  const filteredGroupOptions = groupedOptions
    .map(groupOption => {
      if (variant === ELocationSearchAutocompleteListVariant.Horizontal) {
        if (groupOption.options.length >= DEFAULT_HORIZONTAL_OPTION_MAX) {
          return {
            ...groupOption,
            options: groupOption.options.slice(0, DEFAULT_HORIZONTAL_OPTION_MAX),
          };
        }
        return groupOption;
      } else {
        return {
          ...groupOption,
          options: groupOption.options.slice(0, maxOptionsPerColumn ?? DEFAULT_VERTICAL_OPTION_MAX),
        };
      }
    })
    .slice(0, maxGroups);

  return variant === ELocationSearchAutocompleteListVariant.Horizontal ? (
    <div
      className={clsx('grid gap-8', {
        'grid-cols-1': filteredGroupOptions.length === 1,
        'grid-cols-2': filteredGroupOptions.length > 1,
      })}>
      {filteredGroupOptions.map((groupOption, groupIndex) => (
        <div key={groupIndex}>
          <Text variant={ETextStyleVariant.Label} className="px-2 pb-4 text-left text-gray-500">
            {groupOption.group}
          </Text>
          {/* A `ul` here put a `list` role between the listbox and its options, which
              severed the ownership screen readers rely on (and `ul > div` is invalid
              markup besides). `group` is an allowed child of `listbox` and keeps the
              group name attached to the options it holds. */}
          <div
            role="group"
            aria-label={groupOption.group}
            className={clsx('grid gap-x-4 grid-rows-3 grid-flow-col', {
              'grid-cols-1': groupOption.options.length <= DEFAULT_HORIZONTAL_OPTION_PER_COLUMN_MAX,
              'grid-cols-2': groupOption.options.length > DEFAULT_HORIZONTAL_OPTION_PER_COLUMN_MAX,
            })}>
            {groupOption.options.map((option, optionIndex) =>
              renderOption(
                option,
                optionIndex,
                groupOption.options.length,
                groupIndex === filteredGroupOptions.length - 1,
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className={cn('flex flex-col gap-4 pt-5', className)}>
      {filteredGroupOptions.map((groupOption, groupIndex) => (
        <div key={groupIndex}>
          <Text
            variant={ETextStyleVariant.Label}
            className="px-2 pt-2 pb-4 text-left text-gray-500">
            {groupOption.group}
          </Text>
          <div role="group" aria-label={groupOption.group}>
            {groupOption.options.map((option, optionIndex) =>
              renderOption(
                option,
                optionIndex,
                groupOption.options.length,
                groupIndex === filteredGroupOptions.length - 1,
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
